import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../admin.css';
import { Link, useSearchParams } from 'react-router-dom';
import type { CalcSpec, FieldDefinition, FormRule, FormWidget, PrefillMap, RuleAction, RuleWhen } from '../../shared/types';
import { flattenWidgetTree, isContinuation } from '../../shared/types';
import { BField, BSection, asksNothing, inCont, isStructural, pageOptions, pagesOf, toFieldBody } from './builderModel';
import { useFormWorkspace } from './FormWorkspace';
import BuilderOutline, { BuilderSel, ruleTagDetail } from './BuilderOutline';
import { paletteEntryFor } from './builderPalette';
import { NARROW, PHONE, useMediaQuery } from '../../shared/media';
import { usePageHeight } from './workflow/usePageHeight';
import { isContainerTarget } from '../../form-engine/rules';
import {
  Expand, OptGroup, SecInfo, cardPath, cardsOf, groupsForContainer, repeatScopesIn, sectionsFromSpine,
  subOption, subsOf,
} from './fieldGroups';
import FieldPicker from './FieldPicker';
import { PARENT_PREFIX, ParentForm, parentFieldDefs, parentOptGroups } from './parentFields';
import { getFormParents } from '../../api/forms';

/**
 * ONE rule, ONE button.
 *
 * The page used to have two add buttons, two filter tabs and a badge on every
 * card repeating the same split — for a distinction that only existed because a
 * calculation could not say "when". A rule is now one shape: a trigger plus a
 * list of actions, and Set a value is an action like Show or Prefill. One
 * trigger can run all of them.
 */

const OPS: Record<string, string> = {
  is: 'is', isnot: 'is not', includes: 'includes', notincludes: 'does not include',
  filled: 'is filled in', empty: 'is empty',
  gt: 'is more than', lt: 'is less than', gte: 'is at least', lte: 'is at most',
  before: 'is before', after: 'is after', contains: 'contains',
};

/** The operators that compare against something, and so need a value beside them. */
const NEEDS_VALUE = new Set<RuleWhen['op']>([
  'is', 'isnot', 'includes', 'notincludes', 'gt', 'lt', 'gte', 'lte', 'before', 'after', 'contains',
]);
/** The operators offered by what the watched question holds (P4). Any op a rule already uses stays offered. */
const NUMERIC_OPS: RuleWhen['op'][] = ['gt', 'lt', 'gte', 'lte'];
const DATE_OPS: RuleWhen['op'][] = ['before', 'after'];

/**
 * The list operator that means what a whole-answer one was reached for.
 *
 * `is` and `is not` weigh the entire selection; on a question that takes more
 * than one answer that is rarely the intent, and each has an exact counterpart
 * to point at rather than a general "try something else".
 */
const LIST_TWIN: Partial<Record<RuleWhen['op'], RuleWhen['op']>> = {
  is: 'includes', isnot: 'notincludes',
};

/**
 * Fields whose answer is PICKED from a list, not typed.
 *
 * All three store an option's value and show its label, so a condition on any
 * of them belongs in a dropdown — a text box there asked the person writing
 * the rule to know the stored value, which is exactly what the picker exists
 * to spare them. A checkbox is left out on purpose: it holds a boolean, not
 * one of its `options`.
 */
const CHOICE = new Set(['select', 'radio', 'multiselect']);
const isChoice = (f?: FieldDefinition) => !!f && CHOICE.has(f.input_type) && f.options.length > 0;

/** A question whose answer is a LIST — the one `includes` exists for. */
const isMulti = (f?: FieldDefinition) => f?.input_type === 'multiselect';

/** A question answered with a NUMBER — the ones `more than` / `less than` are for. */
const NUMERIC_TYPES = new Set(['number', 'rating', 'scale']);
const isNumeric = (f?: FieldDefinition) => !!f && NUMERIC_TYPES.has(f.input_type);
/** A question answered with a DATE, with or without a time — the ones `before` / `after` are for. */
const DATE_TYPES = new Set(['date', 'datetime']);
const isDateLike = (f?: FieldDefinition) => !!f && DATE_TYPES.has(f.input_type);
/** Typed text — where `contains` (a case-insensitive search) makes sense. A picked answer is matched whole. */
const isTextLike = (f?: FieldDefinition) =>
  !!f && !isChoice(f) && !isMulti(f) && !isNumeric(f) && !isDateLike(f) && !NO_VALUE_TYPES.has(f.input_type) && f.input_type !== 'checkbox';

/** A matrix or a ranking (P3) is only ever answered-or-not in a condition: its shape has no single value to compare. */
const NO_VALUE_TYPES = new Set(['matrix', 'ranking']);
const isShapeOnly = (f?: FieldDefinition) => !!f && NO_VALUE_TYPES.has(f.input_type);

/**
 * The operator a condition on this question should START on.
 *
 * On a list, `is` weighs the whole selection, so a rule freshly pointed at one
 * would be written against a meaning nobody intended. Picking the question is
 * already the moment the value is rechosen, so the operator moves with it.
 */
const startingOp = (f?: FieldDefinition): RuleWhen['op'] => (isShapeOnly(f) ? 'filled' : isMulti(f) ? 'includes' : 'is');

/** What each verb is called, and which colour family its row belongs to. */
const VERBS: Record<RuleAction['type'], { label: string; family: 'vis' | 'req' | 'pre' | 'set' }> = {
  show: { label: 'Show', family: 'vis' },
  hide: { label: 'Hide', family: 'vis' },
  require: { label: 'Make required', family: 'req' },
  optional: { label: 'Make optional', family: 'req' },
  lock: { label: 'Lock', family: 'pre' },
  unlock: { label: 'Unlock', family: 'pre' },
  prefill: { label: 'Prefill', family: 'pre' },
  calc: { label: 'Set a value', family: 'set' },
  goto: { label: 'Skip to page', family: 'vis' },
};

/**
 * The verb a row can be swapped to WITHOUT losing anything.
 *
 * Show ⇄ Hide and Make required ⇄ Make optional carry an identical `targets`
 * list, so the swap is a relabelling. Prefill and Set a value carry something
 * else entirely, so they are offered no dropdown at all: changing family means
 * removing the row, which is honest about discarding its contents. The old full
 * type select silently threw a Show's targets away on the way to Prefill.
 */
const TWIN: Partial<Record<RuleAction['type'], RuleAction['type']>> = {
  show: 'hide', hide: 'show',
  require: 'optional', optional: 'require',
  lock: 'unlock', unlock: 'lock',
};

/** The three labelled groups of "+ Do something else". */
const ADD_MENU: { group: string; items: { type: RuleAction['type']; label: string }[] }[] = [
  { group: 'Whether it shows', items: [
    { type: 'show', label: 'Show fields' },
    { type: 'hide', label: 'Hide fields' },
  ] },
  { group: 'Whether it is required', items: [
    { type: 'require', label: 'Make required' },
    { type: 'optional', label: 'Make optional' },
  ] },
  { group: 'Whether they can edit it', items: [
    { type: 'lock', label: 'Lock fields — keep the answer, stop the editing' },
    { type: 'unlock', label: 'Unlock fields — hand a locked one back' },
  ] },
  { group: 'What it holds', items: [
    { type: 'prefill', label: 'Prefill from another answer' },
    { type: 'calc', label: 'Set a value — count, sum, maths, a scorecard total, joined text' },
  ] },
  { group: 'Where Next goes', items: [
    { type: 'goto', label: 'Skip to a page — jump past the pages between' },
  ] },
];

const CALC_TYPES: { v: CalcSpec['type']; l: string }[] = [
  { v: 'count', l: 'count of items' },
  { v: 'sum', l: 'sum of a field' },
  { v: 'math', l: 'a maths expression' },
  { v: 'join', l: 'joined text' },
  { v: 'fixed', l: 'fixed text' },
  { v: 'score', l: 'a scorecard total' },
];

/** The three numbers a scorecard wants out of the points on its options. */
const SCORE_MODES: { v: 'earned' | 'available' | 'percent'; l: string }[] = [
  { v: 'earned', l: 'points earned' },
  { v: 'available', l: 'points available' },
  { v: 'percent', l: 'percent of available earned' },
];

/** An id no rule on the form is using yet. */
function freshRuleId(rules: FormRule[]): string {
  const used = new Set(rules.map((r) => r.id));
  let n = rules.length + 1;
  while (used.has(`r${n}`)) n += 1;
  return `r${n}`;
}

/** Automatic per-instance count keys a widget tree exposes to calculations. */
interface CountOption { key: string; label: string }
function countOptions(widgets: FormWidget[]): CountOption[] {
  const out: CountOption[] = [];
  for (const w of widgets) {
    if (!w.tree) continue;
    for (const n of flattenWidgetTree(w.tree)) {
      out.push({ key: n.countKey, label: `${n.itemLabel} count (${n.countKey})` });
    }
  }
  return out;
}

/** A rule's usable conditions — a row with no field is not one. */
const usableConds = (r: FormRule) => (r.when || []).filter((c) => c.field);

export default function LogicAdmin() {
  /**
   * Rules are part of the form, not a thing beside it.
   *
   * They live in the workspace's working copy alongside the canvas, so a rule
   * written here can target a field added on the Build tab a moment ago, and
   * deleting a rule does nothing to the form a customer is filling in until
   * the whole draft is published together.
   */
  const { form, draft, setRules } = useFormWorkspace();
  const rules = draft?.rules || [];
  const [editing, setEditing] = useState<string | null>(null);
  const [trigFilter, setTrigFilter] = useState<'' | 'when' | 'always'>('');
  const [touching, setTouching] = useState('');
  const [error] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [addMenuFor, setAddMenuFor] = useState<string | null>(null);
  const [linkParams, setLinkParams] = useSearchParams();
  /**
   * The forms this one continues, with their published questions — empty for a
   * form that continues nothing.
   *
   * A child form's rules can weigh the order behind them (`parent.orderType`),
   * the way its workflow branches already could, so the same questions have to
   * reach the same pickers here. PUBLISHED questions, like the workflow's: a
   * rule is evaluated against an order that has already been submitted, and
   * that order only holds what was published when it was sent.
   */
  const [parents, setParents] = useState<ParentForm[]>([]);
  useEffect(() => {
    if (!isContinuation(form.settings.continuation)) { setParents([]); return; }
    getFormParents(form.id).then((r) => setParents(r.parents || [])).catch(() => setParents([]));
  }, [form.id, form.settings.continuation]);

  // Everything the pickers offer comes from the same draft the canvas edits.
  const widgets: FormWidget[] = draft?.widgets || [];
  const fields = useMemo<FieldDefinition[]>(
    // A divider is on the canvas but is not a question: it has no value to
    // read, mirror or calculate with, so no picker should offer it.
    () => (draft?.fields || []).filter((f) => !isStructural(f) && !asksNothing(f)).map((f, i) => ({
      ...(toFieldBody(f, (i + 1) * 10) as unknown as FieldDefinition),
      id: f.id ?? 0,
    })),
    [draft?.fields]
  );
  // Sections and cards can be show/hide targets too. Where a card sits — the
  // container it groups and the card it nests in — is one question the canvas
  // answers in the field spine, so the markers there are what to read.
  const sections = useMemo<SecInfo[]>(
    () => sectionsFromSpine(draft?.sections, draft?.fields),
    [draft?.sections, draft?.fields]
  );
  /** The pages a `goto` can name — page 1 is where every route starts, so it is never a target. */
  const pages = useMemo(() => pageOptions(draft?.sections || []), [draft?.sections]);
  const pageLbl = (id: string) => pages.find((p) => p.id === id)?.label || `a page no longer on the form (${id})`;

  /**
   * The parents' questions keyed WITHOUT the prefix, for reading a stored
   * `parent.*` path back into words. The picker's own groups add the prefix;
   * these are what the path is resolved against once it has been stripped.
   */
  const parentBare = useMemo(() => parentFieldDefs(parents, ''), [parents]);
  /**
   * One parent field, or one part of a composite one, by the path a rule
   * stored. A field from further back is keyed `parent.<key>` here, so the
   * path's first dot is not where the key ends — the longest key the path
   * starts with is.
   */
  const parentDefFor = (k: string): { field: FieldDefinition; sub?: string } | null => {
    const rest = k.slice(PARENT_PREFIX.length);
    const whole = parentBare.find((x) => x.key === rest);
    if (whole) return { field: whole };
    const f = parentBare
      .filter((x) => rest.startsWith(`${x.key}.`))
      .sort((a, b) => b.key.length - a.key.length)[0];
    return f ? { field: f, sub: rest.slice(f.key.length + 1) } : null;
  };
  /**
   * How a `parent.*` path reads in a rule sentence.
   *
   * Tagged "the order" rather than left to look like one of this form's own
   * questions: a rule that hides a section on `Order type` means something
   * quite different depending on WHOSE order type, and the sentence is the
   * only place that distinction is ever shown.
   */
  const parentLbl = (k: string): string => {
    const found = parentDefFor(k);
    if (!found) return `${k.slice(PARENT_PREFIX.length)} — an order question that is gone`;
    const part = found.sub
      ? ` › ${subsOf(found.field).find((x) => x.sub === found.sub)?.label || found.sub}`
      : '';
    // A field from further back already names the form holding it.
    return found.field.key.startsWith(PARENT_PREFIX)
      ? `${found.field.label}${part}`
      : `${found.field.label}${part} — the order`;
  };

  const lbl = (k: string) => {
    if (k.startsWith(PARENT_PREFIX)) return parentLbl(k);
    if (k.startsWith('sec:')) {
      const s = sections.find((x) => x.id === k.slice(4));
      return s ? `Section: ${s.title}` : k;
    }
    if (k.startsWith('card:')) {
      const [sid, cid] = k.slice(5).split(':');
      const c = sections.find((x) => x.id === sid)?.cards.find((x) => x.id === cid);
      return c ? `Card: ${c.title || '(untitled)'}` : k;
    }
    if (k.startsWith('wnode:')) return `Widget: every ${scopeName(k.slice(6))}`;
    const dot = k.indexOf('.');
    if (dot > 0) {
      const f = fields.find((x) => x.key === k.slice(0, dot));
      if (f) {
        const s = subsOf(f).find((x) => x.sub === k.slice(dot + 1));
        return `${f.label} › ${s?.label || k.slice(dot + 1)}`;
      }
    }
    return fields.find((x) => x.key === k)?.label || (k || '—');
  };
  /**
   * What a stored condition value is CALLED.
   *
   * A choice field keeps a value ("opt_2") and shows a label ("Second site").
   * The picker offers the labels, so the sentence reading that choice back has
   * to say the same word — it used to read the rule out in a vocabulary the
   * person writing it had never been shown. A value with no matching option —
   * typed by hand, or an option since renamed away — reads as itself.
   */
  const valLbl = (fieldKey: string, value: string): string => {
    // A condition on `parent.orderType` offers the ORDER's own choices, so the
    // sentence reading it back has to look them up there too.
    const where = fieldKey.startsWith(PARENT_PREFIX)
      ? parentDefFor(fieldKey)?.field
      : fields.find((x) => x.key === fieldKey);
    return where?.options?.find((o) => o.value === value)?.text || value;
  };
  const scopeOpts = useMemo(
    () => widgets.flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : []))
      .map((l) => ({ v: l.scope, l: `${l.itemLabel.toLowerCase()} (${l.countKey})` })),
    [widgets]
  );
  const counts = useMemo(() => countOptions(widgets), [widgets]);

  // ---- where things live, for the grouped pickers ----------------------------
  /** Every card on the form that repeats inside `scope`, with its section. */
  const cardsInScope = (scope: string) =>
    sections.flatMap((sec) => cardsOf(sec, scope).map((card) => ({ sec, card })));
  /** What one instance of a repeating container is called. */
  const scopeName = (scope: string) =>
    widgets.flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : []))
      .find((n) => n.scope === scope)?.itemLabel.toLowerCase() || scope;
  const scopeLabel = (scope: string) => `Widget › every ${scopeName(scope)}`;
  /**
   * Repeating containers worth showing: every widget node, plus any scope that
   * still has fields but whose container is gone (their answers are real).
   */
  const repeatScopes = useMemo(() => repeatScopesIn(fields, widgets), [fields, widgets]);

  /**
   * Field options grouped the way the form is built — Section, then
   * Section › Card, then each widget's repeating fields (and their cards) — so
   * two fields that share a label are still tellable apart by where they live.
   * `expand` maps one field to its options (a composite can add its sub-paths).
   */
  const buildGroups = (expand: Expand): OptGroup[] => [
    ...groupsForContainer('order', fields, sections, expand),
    // Cards live inside a widget too, so a repeating scope groups the same way
    // a section does — under a heading naming the thing that repeats.
    ...repeatScopes.flatMap((scope) => groupsForContainer(
      scope, fields, sections, expand, { prefix: scopeLabel(scope), strayLabel: 'Other fields' }
    )),
  ];
  const opt = (f: FieldDefinition) => ({ v: f.key, l: `${f.label} (${f.key})` });

  /** Whole fields only — show/hide targets and the target of a Set. */
  const fieldGroups = useMemo<OptGroup[]>(
    () => buildGroups((f) => [opt(f)]),
    [fields, sections, widgets] // eslint-disable-line react-hooks/exhaustive-deps
  );
  /**
   * The order's questions, offered beside this form's own — LAST, so a picker
   * still opens on the form being edited and the order is somewhere you go
   * looking for.
   *
   * Read-only, and that is the whole shape of it: these groups are added to
   * the pickers a rule READS through (a condition's field, a Set's source, a
   * calc's inputs) and to no picker a rule WRITES through. There is no answer
   * on the order for a rule on this form to change — the order was submitted,
   * possibly weeks ago, by somebody else.
   */
  const parentGroups = useMemo<OptGroup[]>(() => parentOptGroups(parents), [parents]);
  /**
   * Every field a condition can WATCH, this form's and the order's — so
   * choosing `parent.orderType` offers the order's own answer options and the
   * operators that suit its type, rather than falling back to a text box the
   * way an unrecognised key does.
   *
   * The defs come keyed `parent.<key>`, which is exactly how the rule stores
   * the path, so this stays a flat lookup. A path one dot INTO a composite
   * resolves to nothing here, as it always has for this form's own fields —
   * a part of an address is compared as text either way.
   */
  const watchable = useMemo(() => [...fields, ...parentFieldDefs(parents)], [fields, parents]);

  /** Fields plus composite sub-paths — conditions and value sources. */
  const detailGroups = useMemo<OptGroup[]>(
    () => [...buildGroups((f) => [opt(f), ...subsOf(f).map((s) => subOption(f, s))]), ...parentGroups],
    [fields, sections, widgets, parentGroups] // eslint-disable-line react-hooks/exhaustive-deps
  );
  /** Prefill destinations: sub-paths minus the derived combined values. */
  const prefillToGroups = useMemo<OptGroup[]>(
    () => buildGroups((f) => [opt(f), ...subsOf(f).filter((s) => !s.derived).map((s) => subOption(f, s))]),
    [fields, sections, widgets] // eslint-disable-line react-hooks/exhaustive-deps
  );
  /** Detail groups plus the automatic count keys — both can feed a sum/math input. */
  const valueGroups = useMemo<OptGroup[]>(
    () => [
      ...detailGroups,
      ...(counts.length ? [{ label: 'Automatic counts', opts: counts.map((c) => ({ v: c.key, l: `⚙ ${c.label}` })) }] : []),
    ],
    [detailGroups, counts]
  );
  /**
   * Show/hide (and require/optional) targets, arranged exactly like the form:
   * each section (and each card) heads its own group, leads it as a selectable
   * "entire section/card" entry, and its fields follow underneath.
   */
  const targetGroups = useMemo<OptGroup[]>(() => {
    const groups: OptGroup[] = [];
    const knownScopes = new Set(
      widgets.flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : [])).map((n) => n.scope)
    );
    const orderFields = fields.filter((f) => f.scope === 'order');
    const knownSections = new Set(sections.map((s) => s.id));
    for (const s of sections) {
      const inSec = orderFields.filter((f) => f.section === s.id);
      const cards = cardsOf(s, 'order');
      const cardIds = new Set(cards.map((c) => c.id));
      const loose = inSec.filter((f) => !f.ui_meta?.group || !cardIds.has(f.ui_meta.group));
      groups.push({
        label: s.title,
        opts: [{ v: `sec:${s.id}`, l: `▣ ${s.title} — the entire section` }, ...loose.map(opt)],
      });
      for (const c of cards) {
        const cardTitle = cardPath(s, c);
        groups.push({
          label: `${s.title} › ${cardTitle}`,
          opts: [
            { v: `card:${s.id}:${c.id}`, l: `▢ ${cardTitle} — the entire card` },
            ...inSec.filter((f) => f.ui_meta?.group === c.id).map(opt),
          ],
        });
      }
    }
    const stray = orderFields.filter((f) => !knownSections.has(f.section));
    if (stray.length) groups.push({ label: 'Other fields', opts: stray.map(opt) });
    // A card inside a widget hides PER INSTANCE — it can be gone on item 2 and
    // present on item 1 — so it is offered as a target like any other card.
    for (const scope of repeatScopes) {
      const label = scopeLabel(scope);
      const scopeFields = fields.filter((f) => f.scope === scope);
      const cards = cardsInScope(scope);
      const cardIds = new Set(cards.map((x) => x.card.id));
      // The container leads its own group, exactly as a section does: showing
      // or hiding it takes every instance and the add button with it.
      const container = knownScopes.has(scope)
        ? [{ v: `wnode:${scope}`, l: `⌂ every ${scopeName(scope)} — the entire container` }]
        : [];
      groups.push({
        label,
        opts: [...container, ...scopeFields.filter((f) => !f.ui_meta?.group || !cardIds.has(f.ui_meta.group)).map(opt)],
      });
      for (const { sec, card } of cards) {
        const cardTitle = cardPath(sec, card);
        groups.push({
          label: `${label} › ${cardTitle}`,
          opts: [
            { v: `card:${sec.id}:${card.id}`, l: `▢ ${cardTitle} — the entire card, per ${scopeName(scope)}` },
            ...scopeFields.filter((f) => f.ui_meta?.group === card.id).map(opt),
          ],
        });
      }
    }
    return groups;
  }, [fields, sections, widgets]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchRule = (id: string, patch: Partial<FormRule>) =>
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // ---- condition helpers ----
  const setConds = (r: FormRule, when: RuleWhen[]) => patchRule(r.id, { when });
  const addCond = (r: FormRule) => {
    const f = fields.find(isChoice) || fields[0];
    setConds(r, [...(r.when || []), {
      field: f?.key || '', op: startingOp(f), value: (isChoice(f) && f.options[0]?.value) || '',
    }]);
  };
  const patchCond = (r: FormRule, i: number, patch: Partial<RuleWhen>) =>
    setConds(r, (r.when || []).map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const removeCond = (r: FormRule, i: number) => setConds(r, (r.when || []).filter((_, j) => j !== i));

  // ---- action helpers ----
  const setActions = (r: FormRule, actions: RuleAction[]) => patchRule(r.id, { actions });
  const blankAction = (type: RuleAction['type']): RuleAction => {
    if (type === 'prefill') {
      return { type: 'prefill', mode: 'editable', map: fields.length >= 2 ? [{ from: fields[0].key, to: fields[1].key }] : [] };
    }
    if (type === 'calc') {
      const numberField = fields.find((f) => f.input_type === 'number') || fields[0];
      return { type: 'calc', target: numberField?.key || '', calc: { type: 'count', of: scopeOpts[0]?.v || '' } };
    }
    if (type === 'goto') return { type: 'goto', page: pages[1]?.id || '' };
    return { type, targets: [] };
  };
  const addAction = (r: FormRule, type: RuleAction['type']) => {
    setActions(r, [...(r.actions || []), blankAction(type)]);
    setAddMenuFor(null);
  };
  const patchAction = (r: FormRule, i: number, patch: Partial<RuleAction>) =>
    setActions(r, (r.actions || []).map((a, j) => (j === i ? { ...a, ...patch } as RuleAction : a)));
  const patchCalc = (r: FormRule, i: number, a: Extract<RuleAction, { type: 'calc' }>, patch: Record<string, unknown>) =>
    patchAction(r, i, { calc: { ...a.calc, ...patch } } as Partial<RuleAction>);
  /**
   * Replace a prefill's rows wholesale.
   *
   * A row is REPLACED rather than spread over, because its two sources are
   * alternatives: spreading `{ value }` onto a row that still carries `from`
   * would leave both on it, and the reader of that row — here, the engine, the
   * sanitizer — would have to guess which one the author meant.
   */
  const setRows = (r: FormRule, i: number, map: PrefillMap[]) =>
    patchAction(r, i, { map } as Partial<RuleAction>);
  /** The answer options of a prefill target, when it is a choice question. */
  const choiceOptions = (key: string) => {
    const f = fields.find((x) => x.key === key);
    return isChoice(f) ? f!.options : null;
  };
  /** What "put the fixed value" starts at for a target: its first option, or blank. */
  const firstOptionOf = (key: string) => choiceOptions(key)?.[0]?.value ?? '';
  /**
   * The literal to carry when a fixed row is pointed at a different target.
   *
   * Free text survives a move between text questions, and an option survives a
   * move between two questions that both offer it. What cannot survive is an
   * option the new question has never heard of: the row would be storing an
   * answer that question can never hold, so it starts on one that it can.
   */
  const valueFor = (key: string, current: string | undefined): string => {
    const opts = choiceOptions(key);
    if (!opts) return current ?? '';
    return opts.some((o) => o.value === current) ? current! : (opts[0]?.value ?? '');
  };
  const removeAction = (r: FormRule, i: number) => setActions(r, (r.actions || []).filter((_, j) => j !== i));

  /**
   * A new rule, watching `subject` when the builder sent one ("Add a rule
   * about this field") and otherwise the first choice question on the form.
   */
  function addRule(subject?: string) {
    // The id has to be unique against the rules that EXIST, not against a
    // counter that restarts at a fixed number every time this page mounts:
    // that counter handed the same id out again after a reload, and a rule
    // sharing an id with another was edited and deleted together with it.
    const id = freshRuleId(rules);
    // `watchable`, not `fields`: the sidebar can be about an order question
    // (`parent.*`), and a rule about one starts by watching it.
    const asked = subject ? watchable.find((f) => f.key === subject) : undefined;
    const firstChoice = asked ? (isChoice(asked) ? asked : undefined) : fields.find(isChoice);
    const watch = asked || firstChoice || fields[0];
    // A new rule starts with a trigger and NO actions: the actions menu is the
    // next thing to open, and an empty Show row pre-chosen for you is a guess
    // about which of six verbs you wanted.
    setRules((rs) => [{
      id, kind: 'logic', name: '', trigger: 'when', match: 'all',
      when: [{ field: watch?.key || '', op: startingOp(watch), value: firstChoice?.options?.[0]?.value ?? '' }],
      actions: [],
    }, ...rs]);
    setEditing(id);
    setTrigFilter(''); setTouching('');
  }

  /**
   * Links from the builder: `?subject=<key>` opens a new rule about that
   * field, `?rule=<id>` opens that rule. Each is consumed once and stripped
   * from the URL, so a reload does not add a second rule — the same shape as
   * the workflow canvas's `?node=`. The ref is what makes "once" hold under
   * StrictMode, which runs an effect twice before the URL has changed.
   */
  const consumedLink = React.useRef('');
  useEffect(() => {
    const subject = linkParams.get('subject');
    const ruleId = linkParams.get('rule');
    if ((!subject && !ruleId) || !draft) return; // nothing asked, or the working copy is still loading
    const key = linkParams.toString();
    if (consumedLink.current === key) return;
    consumedLink.current = key;
    if (ruleId) {
      if (rules.some((r) => r.id === ruleId)) {
        setEditing(ruleId);
        setTrigFilter(''); setTouching('');
        setTimeout(() => { try { document.querySelector('.lg-card.editing')?.scrollIntoView({ block: 'start' }); } catch { /* jsdom */ } }, 0);
      }
    } else if (subject) addRule(subject);
    const next = new URLSearchParams(linkParams);
    next.delete('subject'); next.delete('rule');
    setLinkParams(next, { replace: true });
  }, [linkParams, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateRule = (r: FormRule) => {
    const id = freshRuleId(rules);
    const copy: FormRule = JSON.parse(JSON.stringify({ ...r, id, name: r.name ? `${r.name} (copy)` : '' }));
    setRules((rs) => {
      const at = rs.findIndex((x) => x.id === r.id);
      return [...rs.slice(0, at + 1), copy, ...rs.slice(at + 1)];
    });
    setMenuFor(null); setEditing(id);
  };
  const deleteRule = (r: FormRule) => {
    setRules((rs) => rs.filter((x) => x.id !== r.id));
    setMenuFor(null);
    setEditing((e) => (e === r.id ? null : e));
  };

  // ---- the sentence ----------------------------------------------------------
  const chip = (s: string, bg: string, fg: string) => (
    <span className="lg-chip" style={{ background: bg, color: fg }}>{s}</span>
  );
  // The sentence's three chip families, on the tokens (R5): a field, a value,
  // a mode or place — the same hues the Outline's ƒ tags and the verbs wear.
  const fld = (k: string) => chip(lbl(k), 'var(--accent-soft)', 'var(--accent-text)');
  const val = (s: string) => chip(`"${s}"`, 'var(--ok-bg)', 'var(--ok-fg)');
  const mode = (s: string) => chip(s, 'var(--identity-bg)', 'var(--identity-fg)');
  const nothing = <span style={{ color: 'var(--text-faint)' }}>(nothing yet)</span>;

  /**
   * ONE sentence for all six verbs, joined with commas and a final "and".
   *
   * The verbs read lower-case inside the sentence because they are ordinary
   * words in it; `make required` / `make optional` and `Always` are bolded
   * because they are the new words, and a reader skimming a page of rules is
   * looking for exactly those.
   */
  function sentence(r: FormRule): React.ReactNode {
    const bold = (t: string) => <span className="lg-tok-bold">{t}</span>;
    const trigger: React.ReactNode[] = [];
    if (r.trigger === 'always') trigger.push(<>{bold('Always')} </>);
    else {
      const conds = usableConds(r);
      if (!conds.length) trigger.push(<span style={{ color: 'var(--text-faint)' }}>No condition yet — this rule never runs. </span>);
      conds.forEach((c, j) => {
        trigger.push(j ? <> {bold(r.match === 'any' ? 'OR' : 'AND')} </> : <>When </>);
        trigger.push(<>{fld(c.field)} {OPS[c.op] || 'is'}{NEEDS_VALUE.has(c.op) ? <> {val(valLbl(c.field, c.value || ''))}</> : null}</>);
      });
      if (conds.length) trigger.push(<>, then </>);
    }

    const clause = (a: RuleAction): React.ReactNode => {
      if (a.type === 'prefill') {
        const map = a.map || [];
        if (!map.length) return <>prefill {nothing}</>;
        return (
          <>
            prefill{' '}
            {map.slice(0, 2).map((m, j) => (
              <React.Fragment key={j}>
                {j ? ' and ' : ''}
                {fld(m.to)} {m.value !== undefined
                  ? <>with {val(valLbl(m.to, m.value))}</>
                  : <>from {fld(m.from || '')}</>}
              </React.Fragment>
            ))}
            {map.length > 2 ? ` +${map.length - 2} more ` : ' '}
            {mode(a.mode === 'locked' ? 'locked' : 'editable')}
          </>
        );
      }
      if (a.type === 'calc') {
        const c = a.calc;
        return (
          <>
            set {a.target ? fld(a.target) : nothing} <span className="lg-tok-bold">=</span>{' '}
            {c.type === 'count' ? <>count of every {mode(c.of ? scopeName(c.of) : '—')} added</>
              : c.type === 'sum' ? <>sum of {fld(c.field)} across every {mode(c.of ? scopeName(c.of) : '—')}</>
              : c.type === 'math' ? <>{fld(c.a)} <span className="lg-tok-bold">{c.op === '*' ? '×' : c.op}</span> {fld(c.b)}</>
              : c.type === 'fixed' ? val(c.value || '')
              : c.type === 'score'
                ? <>{mode(SCORE_MODES.find((m) => m.v === c.mode)?.l || c.mode)} {c.section
                  ? <>in {mode(sections.find((x) => x.id === c.section)?.title || c.section)}</>
                  : <>across the whole form</>}</>
              : c.fields.length
                ? <>{c.fields.map((k, j) => <React.Fragment key={k}>{j ? <span className="lg-tok-bold"> + </span> : null}{fld(k)}</React.Fragment>)} separated by {val(c.sep || ' ')}</>
                : nothing}
          </>
        );
      }
      if (a.type === 'goto') {
        return <><span className="lg-tok-bold">skip to</span> {a.page ? mode(pageLbl(a.page)) : nothing}</>;
      }
      const targets = a.targets || [];
      const verb = a.type === 'require' ? <span className="lg-tok-bold">make required</span>
        : a.type === 'optional' ? <span className="lg-tok-bold">make optional</span>
        : a.type === 'lock' ? <span className="lg-tok-bold">lock</span>
        : a.type === 'unlock' ? <span className="lg-tok-bold">unlock</span>
        : <>{a.type}</>;
      return (
        <>
          {verb}{' '}
          {targets.length
            ? <>{targets.slice(0, 4).map((t) => <React.Fragment key={t}>{fld(t)}</React.Fragment>)}{targets.length > 4 ? ` +${targets.length - 4} more` : ''}</>
            : nothing}
        </>
      );
    };

    const acts = r.actions || [];
    return (
      <>
        {trigger.map((t, i) => <React.Fragment key={i}>{t}</React.Fragment>)}
        {acts.length
          ? acts.map((a, i) => (
            <React.Fragment key={i}>
              {i ? (i === acts.length - 1 ? ' and ' : ', ') : ''}{clause(a)}
            </React.Fragment>
          ))
          : <span style={{ color: 'var(--text-faint)' }}>do nothing yet</span>}
        .
      </>
    );
  }

  /** The head line of a rule with no name: its trigger, in words. */
  const firstClause = (r: FormRule): string => {
    if (r.trigger === 'always') return 'Always';
    const c = usableConds(r)[0];
    if (!c) return 'Unfinished rule';
    const op = OPS[c.op] || 'is';
    return c.op === 'filled' || c.op === 'empty'
      ? `When ${lbl(c.field)} ${op}`
      : `When ${lbl(c.field)} ${op} "${valLbl(c.field, c.value ?? '')}"`;
  };

  // ---- warnings --------------------------------------------------------------
  /**
   * Everything a rule contradicts about itself, in plain sentences. None of
   * these blocks saving — the same treatment the workflow canvas gives an
   * incomplete branch, and for the same reason: a half-written rule is a normal
   * stage of writing one.
   */
  function warningsFor(r: FormRule): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    const of = (type: 'show' | 'hide' | 'require' | 'optional' | 'lock' | 'unlock') => new Set(
      (r.actions || []).flatMap((a) => (a.type === type ? a.targets || [] : []))
    );
    for (const a of r.actions || []) {
      if (a.type !== 'goto') continue;
      if (pages.length < 2) {
        out.push(<>This form has one page, so <b>Skip to page</b> has nowhere to go. Start a new page at a section on the Build tab first.</>);
      } else if (a.page && !pages.some((p) => p.id === a.page)) {
        out.push(<><b>Skip to page</b> names a page that is no longer on the form. Pick another, or remove the row.</>);
      } else if (a.page === pages[0]?.id) {
        out.push(<><b>Skip to page</b> points at page 1, and a skip only ever goes forward — it will do nothing.</>);
      }
    }
    const hidden = of('hide');
    const shown = of('show');
    const required = of('require');
    for (const t of required) {
      if (hidden.has(t)) {
        out.push(
          <><b>{lbl(t)}</b> is in <b>Hide</b> and in <b>Make required</b> in this rule. A hidden field is
          never required — the requirement will do nothing. Remove one.</>
        );
      }
    }
    for (const t of shown) {
      if (hidden.has(t)) {
        out.push(
          <><b>{lbl(t)}</b> is in <b>Show</b> and in <b>Hide</b> in this rule. Hide wins, so the field
          stays away. Remove one.</>
        );
      }
    }
    // Locking a field this rule also writes is the one lock that reads as a
    // mistake: the mirror already disables the input, so the lock adds nothing.
    const mirrored = new Set(
      (r.actions || []).flatMap((a) => (a.type === 'prefill' && a.mode === 'locked' ? (a.map || []).map((m) => m.to) : []))
    );
    for (const t of of('lock')) {
      if (mirrored.has(t)) {
        out.push(
          <><b>{lbl(t)}</b> is in <b>Lock</b> and is already a <b>locked</b> prefill target in this rule.
          A mirrored field is read-only anyway — the lock adds nothing. Remove one.</>
        );
      }
    }
    for (const t of of('lock')) {
      if (of('unlock').has(t)) {
        out.push(
          <><b>{lbl(t)}</b> is in <b>Lock</b> and in <b>Unlock</b> in this rule. Unlock wins, so the
          field stays editable. Remove one.</>
        );
      }
    }
    /**
     * One target filled twice by the same rule, out of two different places.
     *
     * The rows run top to bottom and the lower one silently wins, so the row
     * above it is dead — and nothing on screen admits it, because this page
     * shows no order and gives no way to change one. Two rows filling a target
     * from the SAME place are only a duplicate and say nothing new, so they
     * pass without a word.
     *
     * A part beside its whole field is NOT this. 'Shipping Address › Company'
     * under 'Shipping Address' is how one address gets assembled out of
     * several answers, and the engine settles it by taking the more specific
     * row whichever order the two are in (see `subWrites` in
     * form-engine/rules.ts), so there is nothing left to warn about.
     */
    const filledBy = new Map<string, string[]>();
    for (const a of r.actions || []) {
      if (a.type !== 'prefill') continue;
      for (const m of a.map || []) {
        if (!m.to) continue;
        const src = m.value !== undefined ? `the fixed value “${m.value}”` : (m.from ? lbl(m.from) : '');
        if (!src) continue;
        const seen = filledBy.get(m.to) || [];
        if (!seen.includes(src)) filledBy.set(m.to, [...seen, src]);
      }
    }
    for (const [to, srcs] of filledBy) {
      if (srcs.length < 2) continue;
      out.push(
        <><b>{lbl(to)}</b> is filled twice in this rule — from <b>{srcs[srcs.length - 2]}</b> and
        from <b>{srcs[srcs.length - 1]}</b>. The lower row wins, so the one above it does nothing.
        Remove one.</>
      );
    }
    if (r.trigger === 'when' && !usableConds(r).length) {
      out.push(<>No condition yet — this rule never runs. Add one, or switch it to <b>Always</b>.</>);
    }
    return out;
  }

  // ---- filters ---------------------------------------------------------------
  const trigCounts = { '': rules.length, when: 0, always: 0 } as Record<string, number>;
  rules.forEach((r) => { trigCounts[r.trigger]++; });

  /** Every key a rule reads or writes — what "Touching field" filters on. */
  const keysOf = (r: FormRule): string[] => {
    const ks: string[] = (r.when || []).map((c) => c.field);
    for (const a of r.actions || []) {
      if (a.type === 'prefill') (a.map || []).forEach((m) => { if (m.from) ks.push(m.from); ks.push(m.to); });
      else if (a.type === 'calc') {
        ks.push(a.target);
        if (a.calc.type === 'sum') ks.push(a.calc.field);
        if (a.calc.type === 'math') { ks.push(a.calc.a); ks.push(a.calc.b); }
        if (a.calc.type === 'join') ks.push(...a.calc.fields);
      } else if (a.type === 'goto') { /* a page, not a field */ }
      else ks.push(...(a.targets || []));
    }
    return ks;
  };
  const shown = rules.filter((r) => {
    if (trigFilter && r.trigger !== trigFilter) return false;
    if (touching && !keysOf(r).some((k) => k === touching || k.startsWith(`${touching}.`))) return false;
    return true;
  });

  // ---- shared controls -------------------------------------------------------
  const fieldSelect = (
    value: string, onChange: (v: string) => void,
    groups: OptGroup[] = fieldGroups, style: React.CSSProperties = {}
  ) => (
    <FieldPicker
      className="lg-ctl" style={{ maxWidth: 280, ...style }} value={value} groups={groups}
      placeholder="— pick a field —" allowClear={false} onChange={onChange}
    />
  );

  const targetPicker = (
    targets: string[], onAdd: (v: string) => void, onRemove: (v: string) => void,
    placeholder = '+ Field, section, card or widget…'
  ) => (
    <div className="row">
      {targets.map((t) => (
        <span key={t} className="lg-target-chip">
          {lbl(t)}
          <button className="fc-x" style={{ marginLeft: 4 }} onClick={() => onRemove(t)}>×</button>
        </span>
      ))}
      <select className="lg-add-select" value="" onChange={(e) => { if (e.target.value) onAdd(e.target.value); }}>
        <option value="">{placeholder}</option>
        {targetGroups.map((g, gi) => (
          <optgroup key={gi} label={g.label}>
            {g.opts.map((fo) => <option key={fo.v} value={fo.v}>{fo.l}</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  );

  // ---- editor blocks ---------------------------------------------------------
  /** Block 1: WHEN — the trigger, and only the trigger. */
  function renderTrigger(r: FormRule) {
    return (
      <div className="lg-trigger">
        <div className="lg-trigger-head">
          <span className="lbl">When</span>
          <span className="fs-seg">
            <button type="button" className={r.trigger === 'when' ? 'on' : ''}
              onClick={() => patchRule(r.id, { trigger: 'when' })}>These answers…</button>
            <button type="button" className={r.trigger === 'always' ? 'on' : ''}
              onClick={() => patchRule(r.id, { trigger: 'always' })}>Always</button>
          </span>
          {r.trigger === 'when' && (
            <select className="lg-ctl" value={r.match} onChange={(e) => patchRule(r.id, { match: e.target.value as 'all' | 'any' })}>
              <option value="all">all of these are true</option>
              <option value="any">any of these are true</option>
            </select>
          )}
        </div>
        {r.trigger === 'always' ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 11.5, lineHeight: 1.5 }}>
            This rule runs on every form, for everyone — nothing has to be answered first.
          </div>
        ) : (
          <>
            {/* Conditions stay in state while the trigger says Always, so
                switching back does not lose the work of writing them. */}
            {(r.when || []).map((c, i) => {
              const watched = watchable.find((x) => x.key === c.field);
              const valueOptions = isChoice(watched) ? watched!.options : null;
              /**
               * A saved value the option list does not hold — an option renamed
               * away since, or a multi-answer combination typed back when this
               * was a text box — stays offered as itself. Dropping it would let
               * merely OPENING an old rule rewrite what it watches for.
               */
              const stray = valueOptions && c.value && !valueOptions.some((o) => o.value === c.value) ? c.value : '';
              return (
                <div className="lg-when" key={i} style={{ marginBottom: 6 }}>
                  {fieldSelect(c.field, (v) => {
                    const f = watchable.find((x) => x.key === v);
                    patchCond(r, i, { field: v, op: startingOp(f), value: isChoice(f) ? f!.options[0].value : '' });
                  }, detailGroups)}
                  <select className="lg-ctl" value={c.op} onChange={(e) => patchCond(r, i, { op: e.target.value as RuleWhen['op'] })}>
                    {/* A matrix or ranking (P3) offers only filled / empty; a
                        rule already comparing one keeps its operator on screen. */}
                    {(!isShapeOnly(watched) || c.op === 'is') && <option value="is">is</option>}
                    {(!isShapeOnly(watched) || c.op === 'isnot') && <option value="isnot">is not</option>}
                    {/* `includes` reaches INTO a list, so it is offered where a
                        list is what the answer is. It stays offered on a rule
                        already using it, so changing a question's type does not
                        make the page unable to show its own rule. */}
                    {(isMulti(watched) || c.op === 'includes') && <option value="includes">includes</option>}
                    {(isMulti(watched) || c.op === 'notincludes') && (
                      <option value="notincludes">does not include</option>
                    )}
                    {/* P4: numbers compare as numbers, dates as dates, and typed
                        text can be searched. A picked answer is matched whole. */}
                    {NUMERIC_OPS.filter((op) => isNumeric(watched) || c.op === op).map((op) => (
                      <option key={op} value={op}>{OPS[op]}</option>
                    ))}
                    {DATE_OPS.filter((op) => isDateLike(watched) || c.op === op).map((op) => (
                      <option key={op} value={op}>{OPS[op]}</option>
                    ))}
                    {(isTextLike(watched) || c.op === 'contains') && <option value="contains">contains</option>}
                    <option value="filled">is filled in</option>
                    <option value="empty">is empty</option>
                  </select>
                  {NEEDS_VALUE.has(c.op) && (valueOptions ? (
                    <select className="lg-ctl" value={c.value || ''} onChange={(e) => patchCond(r, i, { value: e.target.value })}>
                      <option value="">(blank)</option>
                      {valueOptions.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                      {stray && <option value={stray}>{stray} — no longer an option</option>}
                    </select>
                  ) : (
                    <input
                      className="lg-ctl" style={{ maxWidth: 180 }} value={c.value || ''}
                      type={NUMERIC_OPS.includes(c.op) ? 'number'
                        : DATE_OPS.includes(c.op) ? (watched?.input_type === 'datetime' ? 'datetime-local' : 'date')
                        : 'text'}
                      step={NUMERIC_OPS.includes(c.op) ? 'any' : undefined}
                      onChange={(e) => patchCond(r, i, { value: e.target.value })}
                    />
                  ))}
                  {(r.when || []).length > 1 && (
                    <button className="fc-x" title="Remove this condition" onClick={() => removeCond(r, i)}>×</button>
                  )}
                  {/* A multi-answer question holds a LIST, and `is` weighs the
                      whole list — so one option matches only when it is the
                      only one ticked. That is a real thing to want and stays
                      available, but it is almost never what someone reaching
                      for `is` here meant, and a rule that silently never fires
                      is worse than a sentence of warning. */}
                  {isMulti(watched) && LIST_TWIN[c.op] && (
                    <div className="lg-when-note">
                      This question can take more than one answer, and <b>{OPS[c.op]}</b> weighs all of
                      them together: it matches {c.op === 'is' ? 'only when' : 'unless'}{' '}
                      <b>{valLbl(c.field, c.value || '')}</b> is the one and only one ticked. Switch to{' '}
                      <b>{OPS[LIST_TWIN[c.op]!]}</b> to weigh that option on its own.
                    </div>
                  )}
                </div>
              );
            })}
            <button className="fc-act" onClick={() => addCond(r)}>+ Condition</button>
          </>
        )}
      </div>
    );
  }

  /** The verb control: a select where the swap is safe, a chip where it is not. */
  function renderVerb(r: FormRule, a: RuleAction, i: number) {
    const { label, family } = VERBS[a.type];
    const twin = TWIN[a.type];
    if (!twin) return <span className={`lg-verb ${family} verb`}>{label}</span>;
    return (
      <select
        className={`lg-verb ${family} verb`}
        value={a.type}
        title="Show and Hide swap in place — the fields you picked stay picked."
        onChange={(e) => patchAction(r, i, { type: e.target.value as RuleAction['type'] } as Partial<RuleAction>)}
      >
        <option value={a.type}>{label}</option>
        <option value={twin}>{VERBS[twin].label}</option>
      </select>
    );
  }

  function renderAction(r: FormRule, a: RuleAction, i: number) {
    return (
      <div className="lg-action" key={i}>
        <span className="verb">{renderVerb(r, a, i)}</span>
        <div className="body">
          {a.type === 'prefill' ? (
            <>
              {(a.map || []).map((m, mi) => {
                const rows = (patch: PrefillMap) => a.map.map((x, j) => (j === mi ? patch : x));
                const isFixed = m.value !== undefined;
                // The row reads left to right in the order it is DECIDED: the
                // field first, then how it gets its answer, then the answer.
                // Everything after the target depends on it — the literal
                // picker is the target's own options, so nobody writes "Yes"
                // at a question whose option is stored as "yes" — and asking
                // for the target last meant going back to redo the box before
                // it every time.
                const toOpts = choiceOptions(m.to);
                const stray = toOpts && m.value && !toOpts.some((o) => o.value === m.value) ? m.value : '';
                return (
                  <div className="row" key={mi}>
                    {fieldSelect(m.to, (v) => setRows(r, i, rows(isFixed ? { to: v, value: valueFor(v, m.value) } : { to: v, from: m.from })), prefillToGroups)}
                    <select
                      className="lg-ctl" style={{ maxWidth: 180 }} aria-label="Where the value comes from"
                      value={isFixed ? 'fixed' : 'answer'}
                      onChange={(e) => setRows(r, i, rows(e.target.value === 'fixed'
                        ? { to: m.to, value: firstOptionOf(m.to) }
                        : { to: m.to, from: m.from || fields[0]?.key || '' }))}
                    >
                      <option value="answer">copy the answer from</option>
                      <option value="fixed">put the fixed value</option>
                    </select>
                    {!isFixed
                      ? fieldSelect(m.from || '', (v) => setRows(r, i, rows({ to: m.to, from: v })), valueGroups)
                      : toOpts ? (
                        <select
                          className="lg-ctl" aria-label="The value to write"
                          value={m.value || ''} onChange={(e) => setRows(r, i, rows({ to: m.to, value: e.target.value }))}
                        >
                          <option value="">(blank)</option>
                          {toOpts.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                          {stray && <option value={stray}>{stray} — no longer an option</option>}
                        </select>
                      ) : (
                        <input
                          className="lg-ctl" style={{ maxWidth: 180 }} aria-label="The value to write"
                          value={m.value || ''} placeholder="the value to write"
                          onChange={(e) => setRows(r, i, rows({ to: m.to, value: e.target.value }))}
                        />
                      )}
                    <button className="fc-x" onClick={() => patchAction(r, i, { map: a.map.filter((_, j) => j !== mi) } as Partial<RuleAction>)}>×</button>
                  </div>
                );
              })}
              <div className="row">
                <button className="fc-act" onClick={() => setRows(r, i, [...(a.map || []), { to: fields[1]?.key || '', from: fields[0]?.key || '' }])}>+ Copy an answer</button>
                <button className="fc-act" onClick={() => setRows(r, i, [...(a.map || []), { to: fields[0]?.key || '', value: firstOptionOf(fields[0]?.key || '') }])}>+ Put a fixed value</button>
                {/* The mode belongs beside the rows it describes, not up in the
                    action head where it was describing nothing in particular. */}
                <select className="lg-ctl" value={a.mode} onChange={(e) => patchAction(r, i, { mode: e.target.value } as Partial<RuleAction>)}>
                  <option value="editable">editable — they can change it</option>
                  <option value="locked">locked — rewritten on every pass</option>
                </select>
              </div>
              <div className="hint">
                A prefill writes into the field's <b>own control</b> — a dropdown stays a dropdown with that
                option chosen, and on <b>editable</b> the respondent can pick another. Reach for{' '}
                <b>Set a value</b> instead when the answer is the form's to work out and never theirs: that one
                is read-only, and it clears itself whenever its rule stops holding.
              </div>
            </>
          ) : a.type === 'calc' ? (
            <>
              <div className="row">
                {fieldSelect(a.target, (v) => patchAction(r, i, { target: v } as Partial<RuleAction>))}
                <span className="lg-tok-bold">=</span>
                <select className="lg-ctl" value={a.calc.type} onChange={(e) => {
                  const t = e.target.value as CalcSpec['type'];
                  const calc: CalcSpec = t === 'score' ? { type: 'score', mode: 'earned' }
                    : t === 'sum' ? { type: 'sum', field: fields[0]?.key || '', of: scopeOpts[0]?.v || '' }
                    : t === 'math' ? { type: 'math', a: fields[0]?.key || '', op: '+', b: fields[1]?.key || '' }
                    : t === 'join' ? { type: 'join', fields: [], sep: ' ' }
                    : t === 'fixed' ? { type: 'fixed', value: '' }
                    : { type: 'count', of: scopeOpts[0]?.v || '' };
                  patchAction(r, i, { calc } as Partial<RuleAction>);
                }}>
                  {CALC_TYPES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
                {a.calc.type === 'count' && (
                  <select className="lg-ctl" value={a.calc.of} onChange={(e) => patchCalc(r, i, a, { of: e.target.value })}>
                    {scopeOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                )}
                {a.calc.type === 'sum' && (
                  <>
                    {fieldSelect(a.calc.field, (v) => patchCalc(r, i, a, { field: v }), valueGroups)}
                    <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>across every</span>
                    <select className="lg-ctl" value={a.calc.of} onChange={(e) => patchCalc(r, i, a, { of: e.target.value })}>
                      {scopeOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </>
                )}
                {a.calc.type === 'math' && (
                  <>
                    {fieldSelect(a.calc.a, (v) => patchCalc(r, i, a, { a: v }), valueGroups)}
                    <select className="lg-ctl" style={{ maxWidth: 70 }} value={a.calc.op} onChange={(e) => patchCalc(r, i, a, { op: e.target.value })}>
                      <option value="+">+</option><option value="-">−</option><option value="*">×</option>
                    </select>
                    {fieldSelect(a.calc.b, (v) => patchCalc(r, i, a, { b: v }), valueGroups)}
                  </>
                )}
                {a.calc.type === 'join' && (
                  <input className="lg-ctl" style={{ maxWidth: 120 }} value={a.calc.sep} placeholder="separator" onChange={(e) => patchCalc(r, i, a, { sep: e.target.value })} />
                )}
                {a.calc.type === 'score' && (() => {
                  const c = a.calc as Extract<CalcSpec, { type: 'score' }>;
                  return (
                    <>
                      <select className="lg-ctl" aria-label="Which scorecard total"
                        value={c.mode} onChange={(e) => patchCalc(r, i, a, { mode: e.target.value })}>
                        {SCORE_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                      </select>
                      <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>in</span>
                      <select className="lg-ctl" aria-label="Which part of the form to score"
                        value={c.section || ''} onChange={(e) => patchCalc(r, i, a, { section: e.target.value || undefined })}>
                        <option value="">the whole form</option>
                        {sections.map((sec) => <option key={sec.id} value={sec.id}>{sec.title || '(untitled section)'}</option>)}
                      </select>
                    </>
                  );
                })()}
                {a.calc.type === 'fixed' && (
                  <input className="lg-ctl" style={{ maxWidth: 220 }} value={a.calc.value} placeholder="the text to write" onChange={(e) => patchCalc(r, i, a, { value: e.target.value })} />
                )}
              </div>
              {a.calc.type === 'join' && (
                <div className="row">
                  {a.calc.fields.map((k) => (
                    <span key={k} className="lg-target-chip">
                      {lbl(k)}
                      <button className="fc-x" style={{ marginLeft: 4 }}
                        onClick={() => patchCalc(r, i, a, { fields: (a.calc as Extract<CalcSpec, { type: 'join' }>).fields.filter((x) => x !== k) })}>×</button>
                    </span>
                  ))}
                  <select className="lg-add-select" value="" onChange={(e) => {
                    const v = e.target.value;
                    const cur = (a.calc as Extract<CalcSpec, { type: 'join' }>).fields;
                    if (v && !cur.includes(v)) patchCalc(r, i, a, { fields: [...cur, v] });
                  }}>
                    <option value="">+ Add a field…</option>
                    {detailGroups.map((g, gi) => (
                      <optgroup key={gi} label={g.label}>
                        {g.opts.map((fo) => <option key={fo.v} value={fo.v}>{fo.l}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}
              <div className="hint">
                A field something sets is read-only on the form. While this rule does not hold,{' '}
                <b>{a.target ? lbl(a.target) : 'the target'}</b> stays empty — no stale value is left behind.
              </div>
              {/* The trap this replaces: reaching for `fixed text` to put an
                  answer into a dropdown, and getting a greyed-out box of text
                  where the dropdown was. That is a prefill, not a total. */}
              {a.calc.type === 'fixed' && choiceOptions(a.target) && (
                <div className="hint">
                  <b>{lbl(a.target)}</b> is a question with answer options, and a field this action sets
                  stops being a dropdown — it renders as a read-only line of text. To put one of its own
                  options into it and keep the dropdown, use{' '}
                  <b>Prefill</b> → <b>put the fixed value</b> instead.
                </div>
              )}
            </>
          ) : a.type === 'goto' ? (
            <>
              <div className="row">
                <select className="lg-ctl" value={a.page} onChange={(e) => patchAction(r, i, { page: e.target.value } as Partial<RuleAction>)}>
                  <option value="">— choose a page —</option>
                  {pages.slice(1).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  {a.page && !pages.some((p) => p.id === a.page) && <option value={a.page}>{pageLbl(a.page)}</option>}
                </select>
              </div>
              <div className="hint">
                When this rule holds as they press <b>Next</b>, the form goes straight to that page. The pages jumped
                over are not asked, not required and not sent; <b>Back</b> returns to the page they came from. The
                jump only reads from a page on or after the one holding this rule's questions, and only forward.
              </div>
            </>
          ) : (
            <>
              {targetPicker(
                a.targets || [],
                (v) => { if (!a.targets.includes(v)) patchAction(r, i, { targets: [...a.targets, v] } as Partial<RuleAction>); },
                (v) => patchAction(r, i, { targets: a.targets.filter((x) => x !== v) } as Partial<RuleAction>)
              )}
              {(a.type === 'require' || a.type === 'optional') && (
                <div className="hint">
                  Only while this rule holds. A field the rule does not reach keeps whatever Build says.
                </div>
              )}
              {a.type === 'lock' && (
                <div className="hint">
                  Read-only while this rule holds. The answer already there <b>stays</b> — a lock takes
                  away the editing, not the value — and a locked field is never demanded, so nobody is
                  blocked by a box they cannot type in.
                </div>
              )}
              {a.type === 'unlock' && (
                <div className="hint">
                  Editable while this rule holds, overruling both a <b>Lock</b> elsewhere and the
                  field's own <b>Locked</b> answer on Build. When two rules disagree, unlock wins.
                </div>
              )}
              {(a.type === 'show' || a.type === 'hide') && (a.targets || []).some(isContainerTarget) && (
                <div className="hint">
                  Hiding a section, card or widget hides everything inside it — and a hidden field is never
                  required, so respondents are not blocked by questions they cannot see.
                </div>
              )}
            </>
          )}
        </div>
        <button className="rm" title="Remove this action" onClick={() => removeAction(r, i)}>×</button>
      </div>
    );
  }

  /** Block 2: THEN — every action, off the one trigger above. */
  function renderEditor(r: FormRule) {
    const warnings = warningsFor(r);
    return (
      <div className="lg-editor">
        {/* The name leads the open card (R5, screen 12): an editable line in
            small caps, blank until somebody gives the rule one — the sentence
            below stays the rule's name until then, exactly as before. */}
        <div className="lx-namerow">
          <input
            className="lx-name" value={r.name} aria-label="Rule name" placeholder="Name this rule (optional)"
            onChange={(e) => patchRule(r.id, { name: e.target.value })}
          />
        </div>
        {warnings.map((w, i) => <div className="lg-warn" key={i}>{w}</div>)}
        {renderTrigger(r)}
        <div className="fb-overline" style={{ margin: '4px 0 8px' }}>Then, all of it</div>
        <div className="lg-actions">
          {(r.actions || []).map((a, i) => renderAction(r, a, i))}
        </div>
        {/* stopPropagation: the page's own click closes both menus, which is
            how clicking anywhere else dismisses them. */}
        <div className="fs-menu-anchor" style={{ marginTop: 10, display: 'block' }} onClick={(e) => e.stopPropagation()}>
          <button className="lg-more" onClick={() => setAddMenuFor(addMenuFor === r.id ? null : r.id)}>
            + Do something else
          </button>
          {addMenuFor === r.id && (
            <div className="fs-menu lg-add-menu">
              {ADD_MENU.map((g) => (
                <React.Fragment key={g.group}>
                  <div className="lg-menu-group">{g.group}</div>
                  {g.items.map((it) => (
                    <button key={it.type} onClick={() => addAction(r, it.type)}>{it.label}</button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- the R5 layout: Outline · the current page · the rule sidebar --------
  //
  // Under 900 the sidebar IS the page — the Outline and the canvas are not
  // rendered (jsdom has no matchMedia, so every existing test is the desktop
  // and sees all three). Between 900 and 1100 the CSS folds the canvas away.
  const narrow = useMediaQuery(NARROW);
  /** The page fills the viewport under the form bar, so the Outline, the
   *  canvas and the rule sidebar each scroll on their own (like Fields). Off
   *  on a phone, where the sidebar is the page. */
  const phone = useMediaQuery(PHONE);
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, !phone);
  const bSections = (draft?.sections || []) as BSection[];
  const bFields = (draft?.fields || []) as BField[];
  const canvasPages = useMemo(() => pagesOf(bSections), [bSections]);
  const [pageIx, setPageIx] = useState(0);
  const curPage = canvasPages[Math.min(pageIx, Math.max(0, canvasPages.length - 1))];
  const tagDetail = useMemo(() => ruleTagDetail(rules), [rules]);
  const widgetSecIds = useMemo(() => new Set(bSections.filter((x) => x.type === 'locations' || x.type === 'widget').map((x) => x.id)), [bSections]);
  /** The Outline's selection is the field the sidebar is about — a whole field, never a sub-path. */
  const rootKey = (k: string) => { const i = k.indexOf('.'); return i < 0 ? k : k.slice(0, i); };
  const outlineSel: BuilderSel = touching
    ? touching.startsWith('sec:') ? { t: 'sec', id: touching.slice(4) }
      : touching.startsWith('card:') ? (() => { const [sec, id] = touching.slice(5).split(':'); return { t: 'card', sec, id } as BuilderSel; })()
        : touching.startsWith('wnode:') ? (() => { const scope = touching.slice(6); const sec = bSections.find((x) => widgetSecIds.has(x.id) && bFields.some((f) => f.nodeScope === scope && f.section === x.id)); return sec ? { t: 'wnode', sec: sec.id, scope } as BuilderSel : null; })()
          : { t: 'field', key: rootKey(touching) }
    : null;
  /** The Outline picks what the sidebar is about; a page node only brings its page up. */
  const pickFromOutline = (sel: BuilderSel) => {
    if (!sel) return;
    if (sel.t === 'field') { setTouching(sel.key); bringUp(sel.key); }
    else if (sel.t === 'sec') {
      const ix = canvasPages.findIndex((p) => bSections.slice(p.start, p.end).some((x) => x.id === sel.id));
      if (ix >= 0) setPageIx(ix);
      // A section is a show/hide target like a field is, so picking one (from
      // its own row or the page row that starts with it) shows the rules on it.
      setTouching(`sec:${sel.id}`);
    } else if (sel.t === 'card') setTouching(`card:${sel.sec}:${sel.id}`);
    else if (sel.t === 'wnode') setTouching(`wnode:${sel.scope}`);
  };
  /** Bring the page holding `key` on screen. */
  const bringUp = (key: string) => {
    const f = bFields.find((x) => x.key === key);
    if (!f) return;
    const ix = canvasPages.findIndex((p) => bSections.slice(p.start, p.end).some((x) => x.id === f.section));
    if (ix >= 0) setPageIx(ix);
  };

  /**
   * The note under a field the rules reach (screen 12): "Triggers 2 rules" on
   * one a condition reads, "Shown if Ticket type is “VIP”" on one an action
   * changes — the first rule that writes it, in the sentence's own words.
   */
  const WRITE_WORDS: Record<string, string> = {
    show: 'Shown', hide: 'Hidden', require: 'Required', optional: 'Optional', lock: 'Locked', unlock: 'Unlocked',
    prefill: 'Prefilled', calc: 'Calculated',
  };
  const writesKey = (a: RuleAction, key: string): boolean => {
    if (a.type === 'prefill') return (a.map || []).some((m) => rootKey(m.to) === key);
    if (a.type === 'calc') return rootKey(a.target) === key;
    if (a.type === 'goto') return false;
    return (a.targets || []).includes(key);
  };
  const condWords = (r: FormRule): string => usableConds(r)
    .map((c) => `${lbl(c.field)} ${OPS[c.op] || 'is'}${NEEDS_VALUE.has(c.op) ? ` “${valLbl(c.field, c.value || '')}”` : ''}`)
    .join(r.match === 'any' ? ' or ' : ' and ');
  const noteFor = (key: string): { text: string; kind: 'reads' | 'writes' } | null => {
    const d = tagDetail.get(key);
    if (!d) return null;
    if (d.reads > 0) return { text: `Triggers ${d.reads} rule${d.reads === 1 ? '' : 's'}`, kind: 'reads' };
    const r = rules.find((x) => (x.actions || []).some((a) => writesKey(a, key)));
    if (!r) return null;
    const a = (r.actions || []).find((x) => writesKey(x, key))!;
    const word = WRITE_WORDS[a.type] || a.type;
    if (r.trigger === 'always') return { text: `${word} always`, kind: 'writes' };
    const words = condWords(r);
    return { text: words ? `${word} if ${words}` : `${word} by a rule that never runs`, kind: 'writes' };
  };
  const anyLogic = tagDetail.size > 0;

  /** One field on the read-only canvas: its label, a ghost of its control, the note. */
  const renderCanvasField = (f: BField) => {
    const note = noteFor(f.key);
    const e = paletteEntryFor(f.type, f.scale);
    const on = touching && rootKey(touching) === f.key;
    const choice = ['select', 'radio', 'multiselect'].includes(f.type);
    return (
      <button
        type="button" key={f.key}
        className={`lx-field${note ? ` logic ${note.kind}` : anyLogic ? ' dim' : ''}${on ? ' on' : ''}`}
        onClick={() => setTouching(f.key)}
        title={note ? note.text : `Rules on ${f.label || f.key}`}
      >
        {f.type === 'heading' || f.type === 'paragraph' || f.type === 'image' || f.type === 'divider' ? (
          <span className={`lx-block lx-block-${f.type}`}>{f.type === 'divider' ? '' : f.label || f.type}</span>
        ) : (
          <>
            <span className="lx-label">
              <span className="lx-glyph" aria-hidden="true">{e?.glyph || '·'}</span>
              {f.label || f.key}{f.req === 'whenShown' && <span className="lx-req"> *</span>}
            </span>
            {choice
              ? <span className="lx-ghost choice">{(f.options || []).slice(0, 3).map((o) => <span key={o.value} className="lx-opt">{o.text}</span>)}{(f.options || []).length > 3 && <span className="lx-opt more">+{f.options.length - 3}</span>}</span>
              : <span className={`lx-ghost${f.type === 'textarea' || f.type === 'signature' ? ' tall' : ''}${f.type === 'checkbox' ? ' check' : ''}`} />}
          </>
        )}
        {note && <span className={`lx-note ${note.kind}`}>{note.text}</span>}
      </button>
    );
  };
  /** A container's items in canvas order: fields, cards (recursing) and widget containers. */
  const renderCanvasCont = (sec: BSection, scope: string, grp: string): React.ReactNode[] => {
    const items = inCont(bFields, { sec: sec.id, scope, grp }, widgetSecIds);
    return items.map((f) => {
      if (f.slot) return null;
      if (f.cardId) {
        const card = sec.cards.find((c) => c.id === f.cardId);
        if (!card) return null;
        const target = `card:${sec.id}:${card.id}`;
        const n = noteFor(target);
        return (
          <div key={f.key} className={`lx-card${n ? ` logic ${n.kind}` : ''}${touching === target ? ' on' : ''}`}>
            <button type="button" className="lx-cont-head" onClick={() => setTouching(target)}>▢ {card.title || 'Card'}{n && <span className={`lx-note ${n.kind}`}>{n.text}</span>}</button>
            <div className="lx-fields">{renderCanvasCont(sec, f.scope, card.id)}</div>
          </div>
        );
      }
      if (f.nodeScope) {
        const target = `wnode:${f.nodeScope}`;
        const n = noteFor(target);
        return (
          <div key={f.key} className={`lx-card widget${n ? ` logic ${n.kind}` : ''}${touching === target ? ' on' : ''}`}>
            <button type="button" className="lx-cont-head" onClick={() => setTouching(target)}>⟳ {scopeName(f.nodeScope)} (repeats){n && <span className={`lx-note ${n.kind}`}>{n.text}</span>}</button>
            <div className="lx-fields">{renderCanvasCont(sec, f.nodeScope, '')}</div>
          </div>
        );
      }
      return renderCanvasField(f);
    });
  };
  const renderCanvasSection = (sec: BSection) => {
    const target = `sec:${sec.id}`;
    const n = noteFor(target);
    const isWidget = widgetSecIds.has(sec.id);
    const w = isWidget ? (sec.type === 'locations' ? (widgets.find((x) => x.id === 'locations') || widgets.find((x) => x.builtin)) : widgets.find((x) => x.id === sec.widgetId)) : undefined;
    return (
      <section key={sec.id} className={`lx-sec${n ? ` logic ${n.kind}` : ''}${touching === target ? ' on' : ''}`}>
        <button type="button" className="lx-cont-head sec" onClick={() => setTouching(target)}>
          {sec.title || sec.id}{n && <span className={`lx-note ${n.kind}`}>{n.text}</span>}
        </button>
        <div className="lx-fields">
          {isWidget && w?.tree
            ? (() => { const target2 = `wnode:${w.tree.scope}`; const n2 = noteFor(target2); return (
              <div className={`lx-card widget${n2 ? ` logic ${n2.kind}` : ''}${touching === target2 ? ' on' : ''}`}>
                <button type="button" className="lx-cont-head" onClick={() => setTouching(target2)}>⟳ {w.tree.itemLabel} (repeats){n2 && <span className={`lx-note ${n2.kind}`}>{n2.text}</span>}</button>
                <div className="lx-fields">{renderCanvasCont(sec, w.tree.scope, '')}</div>
              </div>
            ); })()
            : renderCanvasCont(sec, 'order', '')}
        </div>
      </section>
    );
  };

  const nRules = rules.length;
  const scopeTitle = touching ? `Rules on ${lbl(touching)}` : 'All rules';
  const testHref = `/admin/forms/${form.id}/build?test=1`;

  const cards = shown.map((r) => {
    const isEditing = editing === r.id;
    const n = (r.actions || []).length;
    const warnings = warningsFor(r);
    return (
      <div className={`lg-card ${isEditing ? 'editing' : ''}`} key={r.id}>
        <div className="lg-card-head">
          <span className="lg-name">{r.name || firstClause(r)}</span>
          <span
            className="lg-kind lg-trigger-pill"
            title={`${r.trigger === 'always' ? 'Runs on every form' : 'Waits for its conditions'} · ${n} action${n === 1 ? '' : 's'}`}
          >
            {r.trigger === 'always' ? 'Always' : 'When'} · {n}
          </span>
          {!isEditing && warnings.length > 0 && (
            <span className="lg-kind lg-trigger-pill warn" title="Open this rule to see what is unfinished">needs a look</span>
          )}
          <span className="lg-spacer" />
          <button className="fc-act" onClick={() => { setEditing(isEditing ? null : r.id); setMenuFor(null); }}>
            {isEditing ? 'Done' : 'Edit'}
          </button>
          <span className="fs-menu-anchor" onClick={(e) => e.stopPropagation()}>
            <button className={`fs-btn icon ${menuFor === r.id ? 'open' : ''}`} aria-label="More" title="More"
              onClick={() => setMenuFor(menuFor === r.id ? null : r.id)}>⋯</button>
            {menuFor === r.id && (
              <div className="fs-menu">
                <button onClick={() => duplicateRule(r)}>Duplicate</button>
                <div className="rule" />
                <button className="danger" onClick={() => deleteRule(r)}>Delete rule</button>
              </div>
            )}
          </span>
        </div>
        {/* The sentence is the rule at rest; clicking it opens the card, so a
            rule is one click from read to edit. */}
        <div
          className={`lg-sentence${isEditing ? '' : ' lx-openable'}`}
          onClick={isEditing ? undefined : () => { setEditing(r.id); setMenuFor(null); }}
        >
          {sentence(r)}
        </div>
        {isEditing && renderEditor(r)}
      </div>
    );
  });

  return (
    <div className="lx-page" ref={pageRef} onClick={() => { setMenuFor(null); setAddMenuFor(null); }}>
      <h1 className="fb-vh">Logic</h1>
      {error && <div className="ad-err">{error}</div>}
      <div className="lx-main">
        {!narrow && draft && (
          <div className="lx-side">
            <BuilderOutline
              sections={bSections} fields={bFields} widgets={widgets} rules={rules}
              tagMode="logic" countLabel={`${nRules} rule${nRules === 1 ? '' : 's'}`}
              sel={outlineSel} onSelect={pickFromOutline}
            />
          </div>
        )}
        {!narrow && draft && (
          <div className="lx-canvas">
            <div className="lx-toolbar">
              <div className="fb-pagetabs" role="tablist" aria-label="Pages">
                {canvasPages.map((p, i) => (
                  <button key={p.id} type="button" role="tab" aria-selected={i === pageIx} className={i === pageIx ? 'on' : ''} onClick={() => setPageIx(i)}>
                    {p.n} · {p.title || bSections[p.start]?.title || `Page ${p.n}`}
                  </button>
                ))}
              </div>
              <span className="lx-toolbar-note">These rules run while someone is filling in the form.</span>
            </div>
            <div className="lx-scroll">
              <div className="lx-doc">
                {curPage && (
                  <div className="fb-pagehead">
                    <div className="fb-pagehead-n">Page {curPage.n} of {canvasPages.length}</div>
                    <div className="lx-pagetitle">{curPage.title || bSections[curPage.start]?.title || ''}</div>
                  </div>
                )}
                {curPage && bSections.slice(curPage.start, curPage.end).map(renderCanvasSection)}
                {!bSections.length && <div className="lx-empty-doc">No fields yet. Add some on Fields first.</div>}
              </div>
            </div>
          </div>
        )}
        <aside className="lx-panel" aria-label="Rules">
          <div className="lx-head">
            <b className="lx-title" title={scopeTitle}>{scopeTitle}</b>
            <button className="fs-btn primary sm" onClick={() => addRule(touching && !touching.includes(':') ? (touching.startsWith(PARENT_PREFIX) ? touching : rootKey(touching)) : undefined)}>+ Rule</button>
          </div>
          <div className="fs-seg lx-scope" role="group" aria-label="Which rules">
            <button type="button" className={touching ? 'on' : ''} aria-pressed={!!touching}
              disabled={!touching} title={touching ? undefined : 'Pick a field in the Outline, on the page, or below'}>This field</button>
            <button type="button" className={touching ? '' : 'on'} aria-pressed={!touching} onClick={() => setTouching('')}>All rules · {nRules}</button>
          </div>
          {/* The field the sidebar is about, as a picker too: the Outline
              cannot name a composite's part or an order question. */}
          <select className="lg-ctl lx-fieldsel" aria-label="Field" value={touching} onChange={(e) => { setTouching(e.target.value); if (e.target.value) bringUp(rootKey(e.target.value)); }}>
            <option value="">any</option>
            {detailGroups.map((g, gi) => (
              <optgroup key={gi} label={g.label}>
                {g.opts.map((fo) => <option key={fo.v} value={fo.v}>{fo.l}</option>)}
              </optgroup>
            ))}
          </select>
          {rules.length > 0 && (
            <div className="fs-seg lx-trig" role="group" aria-label="Trigger">
              {([['', 'All'], ['when', 'When'], ['always', 'Always']] as const).map(([v, l]) => (
                <button key={v} type="button" className={trigFilter === v ? 'on' : ''} onClick={() => setTrigFilter(v)}>
                  {l} <span className="count">{trigCounts[v]}</span>
                </button>
              ))}
            </div>
          )}

          <div className="lx-cards">
            {cards}
            {rules.length === 0 && (
              <div className="lg-empty">
                No rules yet. A rule watches the answers and then shows, hides, requires, locks, prefills
                or calculates — as many of those as you need, off one trigger.
                <div className="lg-empty-act">
                  <button className="fs-btn primary" onClick={() => addRule()}>+ Rule</button>
                </div>
              </div>
            )}
            {rules.length > 0 && shown.length === 0 && (
              <div className="lg-empty">
                {touching
                  ? <>No rules on {lbl(touching)} yet. Press <b>+ Rule</b> to add one — for example “When Ticket type is VIP, show Expected arrival time”.</>
                  : 'No rule matches this filter.'}
              </div>
            )}
          </div>

          <div className="lx-foot">
            <Link className="lx-try" to={testHref}>▷ Try the rules in test mode</Link>
            <div className="lx-footnote">
              Rules ride this form&rsquo;s draft, alongside the canvas: they can point at fields you have added but not
              published yet, and adding or deleting one changes nothing for respondents until you <b>Publish</b>.
            </div>
            {counts.length > 0 && (
              <details className="lx-counts">
                <summary>Automatic counts you can use anywhere</summary>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {counts.map((c) => <span key={c.key} className="wb-field-chip" title={`${c.key} — also sent with every submission`}>{c.label}</span>)}
                </div>
                <div className="lx-footnote" style={{ marginTop: 8 }}>
                  Every widget instance carries the counts of everything nested inside it — a location knows how many devices
                  and how many lines it holds, a device knows its own line count. These ride along with every submission, so
                  Monday.com columns, webhooks and emails can map them directly.
                </div>
              </details>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
